import type { ItineraryItem } from '@/services/itinerary';
import type { Trip } from '@/services/trips';
import { getTimezoneForCoordinatesSync } from '@/services/timezones';
import { getDestinationCoordinates as getKnownDestinationCoordinates } from '@/utils/geocodeHelpers';
import { getItemTripCalendarDateKey, parseTripCalendarDateKey } from '@/utils/tripCalendar';

export const parseUtcOffsetHours = (timeZone: string): number | null => {
  const match = /^UTC([+-])(\d{1,2})$/.exec(timeZone);
  if (!match) return null;
  const sign = match[1] === '+' ? 1 : -1;
  return sign * Number.parseInt(match[2], 10);
};

export const isSupportedScheduleTimezone = (timeZone: string): boolean =>
  timeZone.includes('/') || parseUtcOffsetHours(timeZone) !== null;

export const getZonedParts = (date: Date, timeZone: string) => {
  const offsetHours = parseUtcOffsetHours(timeZone);
  if (offsetHours !== null) {
    const shifted = new Date(date.getTime() + offsetHours * 60 * 60 * 1000);
    return {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth() + 1,
      day: shifted.getUTCDate(),
      hour: shifted.getUTCHours(),
      minute: shifted.getUTCMinutes(),
      second: shifted.getUTCSeconds(),
    };
  }

  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? '0');
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
};

const getTimeZoneOffsetMs = (instant: Date, timeZone: string): number => {
  const offsetHours = parseUtcOffsetHours(timeZone);
  if (offsetHours !== null) {
    return offsetHours * 60 * 60 * 1000;
  }
  const zoned = getZonedParts(instant, timeZone);
  const asUTC = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second);
  return asUTC - instant.getTime();
};

/** Wall-clock date/time in an IANA timezone → UTC instant (ms). */
export const zonedDateTimeToUtcMs = (
  y: number,
  m: number,
  d: number,
  hh: number,
  mm: number,
  ss: number,
  timeZone: string,
): number => {
  const offsetHours = parseUtcOffsetHours(timeZone);
  if (offsetHours !== null) {
    return Date.UTC(y, m - 1, d, hh, mm, ss) - offsetHours * 60 * 60 * 1000;
  }

  const utcGuessMs = Date.UTC(y, m - 1, d, hh, mm, ss);
  const guess = new Date(utcGuessMs);
  const offset1 = getTimeZoneOffsetMs(guess, timeZone);
  let corrected = utcGuessMs - offset1;
  const offset2 = getTimeZoneOffsetMs(new Date(corrected), timeZone);
  if (offset2 !== offset1) {
    corrected = utcGuessMs - offset2;
  }
  return corrected;
};

export const toDateKeyInTimezone = (date: Date, timeZone: string): string => {
  const parts = getZonedParts(date, timeZone);
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
};

const parseItemClock = (time?: string): { hour: number; minute: number } | null => {
  if (!time || time === 'flexible') return null;
  const [hourRaw, minuteRaw] = time.split(':');
  const hour = Number.parseInt(hourRaw, 10);
  const minute = Number.parseInt(minuteRaw ?? '0', 10);
  if (!Number.isFinite(hour)) return null;
  return { hour, minute: Number.isFinite(minute) ? minute : 0 };
};

/** Itinerary start times are destination-local wall clock; convert to UTC Date. */
export const resolveItemStartInstant = (
  item: ItineraryItem,
  timeZone: string,
  trip?: Trip,
): Date => {
  const clock = parseItemClock(item.startTime);
  if (!clock) {
    return new Date(item.startDate);
  }

  if (!isSupportedScheduleTimezone(timeZone)) {
    const date = new Date(item.startDate);
    date.setHours(clock.hour, clock.minute, 0, 0);
    return date;
  }

  const dayKey = trip ? getItemTripCalendarDateKey(trip, item) : null;
  const dayParts = dayKey
    ? parseTripCalendarDateKey(dayKey)
    : getZonedParts(new Date(item.startDate), timeZone);
  const ms = zonedDateTimeToUtcMs(
    dayParts.year,
    dayParts.month,
    dayParts.day,
    clock.hour,
    clock.minute,
    0,
    timeZone,
  );
  return new Date(ms);
};

export const resolveItemEndInstant = (item: ItineraryItem, timeZone: string, trip?: Trip): Date | null => {
  if (!item.startTime || item.startTime === 'flexible') return null;

  const endClock = parseItemClock(item.endTime);
  if (endClock && isSupportedScheduleTimezone(timeZone)) {
    const dayKey = trip ? getItemTripCalendarDateKey(trip, item) : null;
    const dayParts = dayKey
      ? parseTripCalendarDateKey(dayKey)
      : getZonedParts(new Date(item.startDate), timeZone);
    const ms = zonedDateTimeToUtcMs(
      dayParts.year,
      dayParts.month,
      dayParts.day,
      endClock.hour,
      endClock.minute,
      0,
      timeZone,
    );
    return new Date(ms);
  }

  const start = resolveItemStartInstant(item, timeZone, trip);
  const fallback = new Date(start);
  fallback.setTime(fallback.getTime() + 2 * 60 * 60 * 1000);
  return fallback;
};

export const resolveTripSurfaceTimezone = (trip: Trip): string => {
  const destination = trip.destinations?.[0];
  if (destination?.coordinates) {
    const { latitude, longitude } = destination.coordinates;
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return getTimezoneForCoordinatesSync({ latitude, longitude });
    }
  }
  if (trip.destinationCoordinates) {
    const { latitude, longitude } = trip.destinationCoordinates;
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return getTimezoneForCoordinatesSync({ latitude, longitude });
    }
  }

  const label = destination?.city || trip.destination || '';
  const known = label ? getKnownDestinationCoordinates(label) : null;
  if (known) {
    return getTimezoneForCoordinatesSync({ latitude: known.lat, longitude: known.lng });
  }

  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
};
